import React, { useEffect, useState, useContext } from "react";
import styled from "@emotion/styled";
import {
  Box,
  Button,
  TextField,
  Typography,
  Autocomplete,
  Grid,
  Paper,
  Tabs,
  Tab,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
  Icon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
} from "@mui/material";
import TopPanelComponent from "components/shared/CodingGroupComponents/TopPanelComponent";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import DiagnosisCodeDialog from "components/shared/Dialogs/DiagnosisCodeDialog";
import ProcedureCodeDialog from "components/shared/Dialogs/ProcedureCodeDialog";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import HealthPlanMultiSelect from "components/common/HealthPlanMultiSelect";
import Drawers from "components/common/Drawers/index";
import GreyOutButton from "components/GreyOutButton/GreyOutButton";
import Api from "api";
import { useDispatch } from "react-redux";
import { setDialog, setLoading } from "slices/appSlice";
import { formErrorStrfromObj } from "utils/shared";
import { UserContext } from "context";
import { useSelector } from "react-redux";
import LoadingBackdrop from "components/LoadingBackDrop";
import PoetIcon from "components/PoetIcon/PoetIcon";
import LatestVersionDialog from "./LatestVersionDialog";
import { has } from "lodash";

const ActionButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  textTransform: "uppercase",
}));

const AHCS_HEALTH_PLAN_KEY = 999;

export default function CodingGroupEditor() {
  const [codeGroup, setCodeGroup] = useState(null);
  const user = useContext(UserContext);
  const [rowData, setRowData] = useState([]);
  const canEditOrCreate =
    user?.groups?.includes("POET-Avalon-Admin") ||
    (user?.groups?.includes("POET-Avalon-POE") &&
      (user?.POETSkill?.includes("Coding") ||
        user?.POETSkill?.includes("Manager")));

  const [formData, setFormData] = useState({
    groupName: "",
    description: "",
    effectiveVersions: [],
    effectiveDate: null,
    changeDescription: "",
    typeOfChange: "",
    expandedCodes: [], // these two will include both diagnosis and procedure codes
    condensedCodes: [],
    isVarianceOf: null,
    isVariance: false,
    codeGroupStatus: "",
  });

  const [typeofchangePolicies, setTypeofchangePolicies] = useState([]);
  const [showVariancePrompt, setNewVariancePrompt] = useState(false);

  const handleTabChange = (event, newValue) => {
    setDiagnosisTab(newValue);
  };
  // Determine if fields should be editable (not Approved status and has permission)
  const status = formData?.codeGroupStatus || "";
  const isEditable =
    canEditOrCreate && status.toUpperCase() !== "Approved".toUpperCase();

  const disableCreatevariance =
    canEditOrCreate && status.toUpperCase() == "Approved".toUpperCase();

  const isApproved =
    typeof formData.codeGroupStatus === "string" &&
    formData.codeGroupStatus.toUpperCase() === "APPROVED";
  const canEditHealthPlans = Boolean(
    canEditOrCreate && (isApproved || formData.isVariance)
  );
  const disableCreateVarianceAndCreateNewVersion = codeGroup == null;

  const shouldShow = isEditable && isApproved;
  const loading = useSelector((state) => state.app.loading);
  const navigate = useNavigate();
  const [diagnosisTab, setDiagnosisTab] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isPxDialogOpen, setIsPxDialogOpen] = useState(false);
  const [pxCodeList, setPxCodeList] = useState([]);
  const [validationErrors, setValidationErrors] = useState({
    description: "",
    changeDescription: "",
    codes: "",
  });
  const [openDxDialog, setOpenDxDialog] = useState(false);
  const [healthPlans, setHealthPlans] = useState([]);
  const [activePolicies, setActivePolicies] = useState([]);
  const [reviewData, setReviewData] = useState({
    ctReviewKey: undefined,
    reviewType: "Code Group",
    results: "",
    comments: "",
  });
  const [ctReviewRows, setCtReviewRows] = useState([]);
  const [policyHistoryData, setPolicyHistoryData] = useState([]);
  const [activeDrawer, setActiveDrawer] = useState([]);
  const [haveInactiveCodes, setHaveInactiveCodes] = useState(false);
  // Dialog state for navigating to latest version when requested
  const [newVersionPrompt, setNewVersionPrompt] = useState({
    open: false,
    latestKey: null,
  });
  const dispatch = useDispatch();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search || "");
  const page = location.state?.page || searchParams.get("page") || "";

  const navigateToList = () => {
    if (page === "Diagnosis Groups") {
      navigate("/diagnosis-groups");
    } else if (page === "Procedure Groups") {
      navigate("/procedure-groups");
    } else {
      navigate(-1);
    }
  };

  const handleCancel = () => {
    navigateToList();
  };

  const params = useParams();
  const codeGroupKey = params?.codeGroupKey || "";
  const dxCodes = [];

  const varianceLocked = Array.isArray(healthPlans) && healthPlans.length > 0;
  const varianceRequired = Boolean(formData?.isVariance || varianceLocked);
  const isApprovedAndVariance = isApproved && Boolean(formData?.isVariance);
  const isLatest =
    !codeGroup?.latestCodeGroup ||
    codeGroup?.codeGroupKey === codeGroup?.latestCodeGroup?.codeGroupKey;

  const hasLinkedPolicies =
    Array.isArray(codeGroup?.linkedPolicies) &&
    codeGroup?.linkedPolicies.length > 0;
  const parentHasLinkedPolicies =
    Array.isArray(codeGroup?.parentCodeGroup?.linkedPolicies) &&
    codeGroup?.parentCodeGroup?.linkedPolicies.length > 0;
  const hasEffectiveDate = Boolean(formData.effectiveDate);
  const hasEffectiveVersions =
    Array.isArray(formData.effectiveVersions) &&
    formData.effectiveVersions.length > 0;

  const isDraftStatus = (nextStatus) =>
    typeof nextStatus === "string" && nextStatus.toUpperCase() === "DRAFT";

  const clearValidationError = (field) => {
    setValidationErrors((prev) =>
      prev[field] ? { ...prev, [field]: "" } : prev
    );
  };

  const getCodesValidationMessage = () =>
    page === "Procedure Groups"
      ? "At least one procedure code is required."
      : "At least one diagnosis code is required.";

  const hasRequiredCodesForPage = () =>
    page === "Procedure Groups"
      ? pxCodeList.length > 0
      : formData.expandedCodes.length > 0;

  const validateNonDraftSave = (nextStatus) => {
    if (isDraftStatus(nextStatus)) {
      setValidationErrors({
        description: "",
        changeDescription: "",
        codes: "",
      });
      return true;
    }

    const nextErrors = {
      description: formData.description.trim()
        ? ""
        : "Description is required.",
      changeDescription: formData.changeDescription.trim()
        ? ""
        : "Change Description is required.",
      codes: hasRequiredCodesForPage() ? "" : getCodesValidationMessage(),
    };

    setValidationErrors(nextErrors);

    const rawMessages = Object.values(nextErrors).filter(Boolean);
    if (!hasEffectiveDate && !hasEffectiveVersions) {
      rawMessages.push(
        "Either Effective Date or Effective With Policy Version is required."
      );
    }

    if (rawMessages.length > 0) {
      dispatch(
        setDialog({
          title: "Validation Error",
          message: rawMessages.join(", "),
        })
      );
      return false;
    }

    return true;
  };

  const saveGroup = async (
    nextStatus = formData.codeGroupStatus,
    successMessage = "Group saved successfully.",
    navigateBack = true
  ) => {
    if (!validateNonDraftSave(nextStatus)) {
      return;
    }

    const trimmedDescription = formData.description.trim();
    const trimmedChangeDescription = formData.changeDescription.trim();

    dispatch(setLoading(true));
    //send ct review data if in pending review and reviewData has been modified (results or comments)
    if (
      isPendingReview &&
      reviewData.ctReviewKey &&
      (reviewData.results || reviewData.comments)
    ) {
      try {
        await Api.CodeGroup.saveCtReview(
          codeGroupKey,
          page === "Procedure Groups" ? "procedure" : "diagnosis",
          reviewData.ctReviewKey,
          reviewData
        );
      } catch (error) {
        const message =
          formErrorStrfromObj(error) ||
          "An error occurred while saving the C&T Review.";
        dispatch(setDialog({ message, title: "Error" }));
        dispatch(setLoading(false));
        return;
      }
    }

    try {
      const selectedHpKeys = (
        Array.isArray(healthPlans) ? healthPlans : []
      ).map((k) => (Number.isFinite(Number(k)) ? Number(k) : k));
      const requestBody = {
        codeGroupKey: toIntOrNull(codeGroupKey),
        codeGroupId: toIntOrNull(codeGroup?.codeGroupId),
        codeGroupName: formData.groupName,
        codeGroupDescription: trimmedDescription,
        changeDescription: trimmedChangeDescription,
        codeGroupType: page === "Procedure Groups" ? "PROCEDURE" : "DIAGNOSIS",
        codeGroupVersion:
          codeGroup?.codeGroupVersion != null
            ? codeGroup.codeGroupVersion
            : 1.0,
        effectiveDate: formData.effectiveDate
          ? toLocalDateArray(formData.effectiveDate)
          : null,
        effectiveVersions: formData.effectiveVersions,
        // Only include healthPlans when variance is required AND the group is Approved
        // (Health Plans may only be added/changed in Approved status)
        ...(varianceRequired && canEditHealthPlans
          ? { healthPlans: selectedHpKeys }
          : {}),
        endDate: null,
        typeOfChange: formData.typeOfChange || null,
        codeGroupStatus: nextStatus,
        codeCount: formData.expandedCodes.length,
        // UI-backed field (Variance yes/no)
        isVariance: Boolean(formData?.isVariance),
        // Round-trip only (do not drive UI from it)
        isVarianceOf: toIntOrNull(
          codeGroup?.isVarianceOf ?? formData?.isVarianceOf
        ),
        policyCount: null,
        ruleUnitCount: null,
        lastUpdate: toLocalDateTimeArray(dayjs()),
        codeMappings:
          page === "Diagnosis Groups"
            ? {
                icdKeys: formData.expandedCodes.map(
                  (code) => code.icdDiagnosisKey
                ),
                effectiveDateTime: formData.effectiveDate
                  ? toLocalDateTimeArray(formData.effectiveDate.startOf("day"))
                  : null,
                endDateTime: null,
              }
            : page === "Procedure Groups"
              ? {
                  procedureCodes: pxCodeList.map((code) => code.code),
                  effectiveDateTime: formData.effectiveDate
                    ? toLocalDateTimeArray(
                        formData.effectiveDate.startOf("day")
                      )
                    : null,
                  endDateTime: null,
                }
              : undefined,
      };
      if (page === "Procedure Groups") {
        await Api.CodeGroup.saveCodeGroup(
          codeGroupKey,
          "procedure",
          requestBody
        );
      } else {
        await Api.CodeGroup.saveCodeGroup(
          codeGroupKey,
          "diagnosis",
          requestBody
        );
      }
      setFormData((prev) => ({ ...prev, codeGroupStatus: nextStatus }));
      dispatch(setDialog({ message: successMessage, title: "Success" }));
      if (navigateBack) {
        navigateToList();
      } else {
        await fetchCodeGroupDetails(codeGroupKey);
      }
    } catch (error) {
      const message =
        formErrorStrfromObj(error) || "An error occurred during the save.";
      dispatch(setDialog({ message, title: "Error" }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const fetchCodeGroupDetails = async (key) => {
    dispatch(setLoading(true));
    try {
      const response = await Api.CodeGroup.getCodeGroupDetails(
        key,
        page === "Procedure Groups" ? "procedure" : "diagnosis"
      );
      const groupDetails = response.data;
      const condensedCodes = groupDetails.condensedCodes.map((code) => {
        if (typeof code === "object") {
          return {
            code: code.code.trim(),
            description: code.description ? code.description.trim() : "",
          };
        }

        return { code: code.trim(), description: "" };
      });
      let expandedCodes = groupDetails.expandedCodes.map((code) => ({
        icdDiagnosisKey: code.icdDiagnosisKey ?? null,
        code: code.code.trim(),
        description: code.description ? code.description.trim() : "",
        inactive: code.inactive || false,
      }));

      // For diagnosis groups: getCodeGroupDetails may not return icdDiagnosisKey on
      // expandedCodes items. Enrich them via getDiagnosisCodeDescriptions (same API
      // the DiagnosisCodeDialog uses) so that saves without editing the code list
      // send valid keys instead of nulls.
      if (page !== "Procedure Groups" && expandedCodes.length > 0) {
        const anyMissingKey = expandedCodes.some(
          (code) => code.icdDiagnosisKey == null
        );
        if (anyMissingKey) {
          const codeStrings = expandedCodes.map((code) => code.code);
          const descriptions =
            await Api.diagnosisCode.getDiagnosisCodeDescriptions(codeStrings);
          expandedCodes = expandedCodes.map((code, index) => ({
            ...code,
            icdDiagnosisKey:
              code.icdDiagnosisKey ??
              descriptions.data[index]?.icdDiagnosisKey ??
              null,
          }));
        }
      }

      let hpKeys = [];
      if (Array.isArray(groupDetails.healthPlans)) {
        if (
          groupDetails.healthPlans.length > 0 &&
          typeof groupDetails.healthPlans[0] === "object"
        ) {
          hpKeys = groupDetails.healthPlans
            .map((hp) => hp?.healthPlanKey)
            .filter((k) => k != null);
        } else {
          hpKeys = groupDetails.healthPlans;
        }
      }

      const ctReviews = getCtReviewRows(groupDetails);
      const pendingReviewRow =
        ctReviews.find((row) => row.results === null) || ctReviews[0];

      setCodeGroup(groupDetails);
      setHaveInactiveCodes(expandedCodes.some((code) => code.inactive));
      setCtReviewRows(ctReviews);
      setReviewData((prev) => ({
        ...prev,
        ctReviewKey: pendingReviewRow?.ctReviewKey,
        results: pendingReviewRow?.results || "",
        comments: pendingReviewRow?.comments || "",
      }));
      setFormData({
        groupName: groupDetails.codeGroupName || "",
        description: groupDetails.codeGroupDescription || "",
        effectiveVersions: groupDetails.effectiveVersions || [],
        effectiveDate: groupDetails.effectiveDate
          ? dayjs(groupDetails.effectiveDate, "YYYY-MM-DD")
          : null,
        changeDescription: groupDetails.changeDescription || "",
        isVarianceOf: toIntOrNull(groupDetails.isVarianceOf),
        isVariance: Boolean(groupDetails.isVariance),
        typeOfChange:
          groupDetails.typeOfChange || groupDetails.typeOfChangeText || "",
        condensedCodes: condensedCodes || [],
        expandedCodes: expandedCodes || [],
        codeGroupStatus: groupDetails.codeGroupStatus || "",
      });
      setValidationErrors({
        description: "",
        changeDescription: "",
        codes: "",
      });
      setPxCodeList(groupDetails.expandedCodes || []);

      const isVarianceFromDetails = Boolean(groupDetails.isVariance);

      setHealthPlans(hpKeys);
      fetchActivePolicies(hpKeys, isVarianceFromDetails);
    } catch (error) {
      const message =
        formErrorStrfromObj(error) || "An error occurred during the fetch.";
      dispatch(setDialog({ message, title: "Error" }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const fetchActivePolicies = async (
    hpKeys = healthPlans,
    isVarianceOverride = formData?.isVariance
  ) => {
    if (isVarianceOverride && (!Array.isArray(hpKeys) || hpKeys.length === 0)) {
      setActivePolicies([]);
      return;
    }
    dispatch(setLoading(true));
    try {
      const effectiveHpKeys = isVarianceOverride
        ? hpKeys
        : [AHCS_HEALTH_PLAN_KEY];
      const [response, response2] = await Promise.all([
        Api.CodeGroup.allActivePolicies(effectiveHpKeys),
        Api.CodeGroup.TypeofchangeAllPolicies(),
      ]);
      setActivePolicies(response.data);
      setTypeofchangePolicies(response2.data);
    } catch (error) {
      const message =
        formErrorStrfromObj(error) || "An error occurred during the fetch.";
      dispatch(setDialog({ message, title: "Error" }));
    } finally {
      dispatch(setLoading(false));
    }
  };
  useEffect(() => {
    if (codeGroupKey) {
      fetchCodeGroupDetails(codeGroupKey);
    }
  }, [codeGroupKey]);

  useEffect(() => {
    // New-group flow has no key, so load default AHCS-backed policy options.
    if (!codeGroupKey) {
      fetchActivePolicies([AHCS_HEALTH_PLAN_KEY], false);
    }
  }, [codeGroupKey]);

  const handleAddEditCodes = () => {
    if (page === "Procedure Groups") {
      setIsPxDialogOpen(true);
    } else {
      setDialogOpen(true);
    }
  };

  const fetchCollapsedRange = async (codes) => {
    if (!codes.length) {
      return [];
    }
    dispatch(setLoading(true));
    try {
      const response = await Api.policyCoding.getDxCollapsed(codes);
      const list = response.data;
      return list.length ? list : [];
    } catch (error) {
      const message =
        formErrorStrfromObj(error) || "An error occurred during the search.";
      dispatch(setDialog({ message, title: "Error" }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSaveDx = async (newDxCodes) => {
    const condenseList = await fetchCollapsedRange(
      newDxCodes.map((item) => item.code)
    );
    const condenseListObjectArray = condenseList.map((codeStr) => {
      const existing = newDxCodes.find((item) => item.code === codeStr);
      return {
        icdDiagnosisKey: existing ? existing.icdDiagnosisKey : null,
        code: codeStr,
        description: existing ? existing.description : "",
      };
    });

    setFormData((prevData) => ({
      ...prevData,
      condensedCodes: condenseListObjectArray.length
        ? condenseListObjectArray
        : [],
      expandedCodes: newDxCodes.length ? newDxCodes : [],
    }));
    if (newDxCodes.length > 0) {
      clearValidationError("codes");
    }

    setDialogOpen(false);
  };

  const handleAddPxCodesToGrid = (pxCodes) => {
    const seen = new Set();
    const deduped = pxCodes.filter(({ code }) => {
      if (seen.has(code)) return false;
      seen.add(code);
      return true;
    });
    setPxCodeList(deduped);
    if (deduped.length > 0) {
      clearValidationError("codes");
    }
    setOpenDxDialog(false);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "effectiveDate" && value
        ? { effectiveVersions: [], typeOfChange: "" }
        : {}),
    }));

    if (typeof value === "string" && value.trim()) {
      if (field === "description" || field === "changeDescription") {
        clearValidationError(field);
      }
    }
  };
  const handleChangeForEffectiveVersions = (value) => {
    if (Array.isArray(value)) {
      setFormData((prev) => ({
        ...prev,
        effectiveVersions: value,
        ...(value.length > 0 ? { effectiveDate: null } : {}),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        effectiveVersions: [...prev.effectiveVersions, value],
        effectiveDate: null,
      }));
    }
  };

  const handleDrawerButtonClick = (drawerType) => {
    setActiveDrawer(drawerType);
  };

  const closeDrawer = () => {
    setActiveDrawer(null);
  };

  const createVariance = async () => {
    if (!codeGroupKey) return;
    dispatch(setLoading(true));
    try {
      const resolvedGroupName =
        (formData && formData.groupName) || codeGroup?.codeGroupName || "";

      const requestBody = {
        codeGroupId: toIntOrNull(codeGroup?.codeGroupId ?? codeGroupKey),
        codeGroupVersion: codeGroup?.codeGroupVersion ?? 1.0,
        newGroupName: resolvedGroupName,
        newCodeGroupName: resolvedGroupName,
        codeGroupName: resolvedGroupName,
        groupName: resolvedGroupName,
        status: "Draft",
        isVariance: true,
        isVarianceOf: toIntOrNull(codeGroup?.codeGroupKey ?? codeGroupKey),
      };

      let response;
      try {
        response = await Api.CodeGroup.createVarianceCodeGroup(
          codeGroupKey,
          page === "Procedure Groups" ? "procedure" : "diagnosis",
          requestBody
        );
      } catch (err) {
        console.error("Error creating variance:", err);
        throw err;
      }
      const newCodeGroup = response?.data;
      if (!newCodeGroup) {
        throw new Error("No variance returned from server");
      }
      // If API returns a new key, re-fetch that code group to refresh screen
      const newKey =
        newCodeGroup.codeGroupKey || newCodeGroup.codeGroupId || codeGroupKey;
      if (!newCodeGroup.status) newCodeGroup.status = "Draft";
      if (typeof newCodeGroup.isVariance !== "boolean") {
        newCodeGroup.isVariance = Boolean(newCodeGroup.isVariance);
      }

      // Hard reload to the newly created variance using its key in the URL
      // Preserve the current "page" via query param so component can infer type on reload
      window.location.href = `/coding-group-editor/${newKey}?page=${encodeURIComponent(
        page || ""
      )}`;

      dispatch(
        setDialog({
          title: "Success",
          message: "Variance created. Redirecting to new group...",
        })
      );
    } catch (error) {
      const message =
        formErrorStrfromObj(error) ||
        "An error occurred while creating variance.";
      dispatch(setDialog({ message, title: "Error" }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Placeholder for future implementation
  const createNewVersion = async () => {
    if (!codeGroupKey) return;

    dispatch(setLoading(true));
    let response;
    try {
      response = await Api.CodeGroup.requestNewVersion(
        codeGroupKey,
        page === "Procedure Groups" ? "procedure" : "diagnosis"
      );
      const newCodeGroupKey = response?.data?.codeGroupKey;
      if (!newCodeGroupKey) {
        const message = "No codeGroupKey returned from server.";
        dispatch(setDialog({ message, title: "Error" }));
      } else {
        window.location.href = `/coding-group-editor/${newCodeGroupKey}?page=${encodeURIComponent(
          page || ""
        )}`;
      }
    } catch (error) {
      const message =
        formErrorStrfromObj(error) ||
        "An error occurred while requesting new version.";
      const match = message && message.match(/key\s+(\d+)/i);
      if (match && match[1]) {
        const existingKey = match[1];
        setNewVersionPrompt({ open: true, latestKey: existingKey });
        // Do not show error dialog; prompt user instead
        return;
      }
      dispatch(setDialog({ message, title: "Error" }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  if (loading) {
    return <LoadingBackdrop />;
  }

  function getPolicyByKey(policyKey) {
    return activePolicies.find((policy) => policy.policyKey === policyKey);
  }

  const isPendingReview =
    typeof formData.codeGroupStatus === "string" &&
    formData.codeGroupStatus.toUpperCase() === "PENDING REVIEW";

  const isDraft =
    typeof formData.codeGroupStatus === "string" &&
    formData.codeGroupStatus.toUpperCase() === "DRAFT";

  const canUpdateCtReview = canEditOrCreate && isPendingReview;

  const saveHpChanges = async () => {
    try {
      dispatch(setLoading(true));
      await Api.CodeGroup.addHealthPlanToCodeGroup(
        codeGroupKey,
        page === "Procedure Groups" ? "procedure" : "diagnosis",
        healthPlans
      );
      dispatch(
        setDialog({
          message: "Health Plans added to Code Group successfully.",
          title: "Success",
          onClose: () => {
            navigate(-1);
          },
        })
      );
    } catch (error) {
      formErrorStrfromObj(error) ||
        dispatch(
          setDialog({
            message:
              formErrorStrfromObj(error) ||
              "An error occurred while saving health plans.",
            title: "Error",
          })
        );
    } finally {
      dispatch(setLoading(false));
    }
  };
  const submitForReview = () => {
    // When an Effective Date is present, Type of Change must be selected
    if (
      varianceRequired &&
      canEditHealthPlans &&
      (!healthPlans || healthPlans.length === 0)
    ) {
      dispatch(
        setDialog({
          title: "Validation Error",
          message: "Health Plans must be selected when Variance is set to Yes.",
        })
      );
      return;
    }
    const effectiveSet = Boolean(formData.effectiveDate);
    if (
      effectiveSet &&
      (!formData.typeOfChange || formData.typeOfChange === "")
    ) {
      dispatch(
        setDialog({
          title: "Validation Error",
          message: "Type of Change is required when Effective Date is set.",
        })
      );
      return;
    }
    if (isPendingReview) {
      if (!["pass", "fail"].includes(reviewData.results)) {
        dispatch(
          setDialog({
            message: "C&T Reviews must be completed to save and submit.",
          })
        );
        return;
      }

      if (reviewData.results === "fail" && !reviewData.comments.trim()) {
        dispatch(
          setDialog({
            message: "Comments are required when C&T Reviews Result is 'Fail'.",
          })
        );
        return;
      }

      const nextStatus = reviewData.results === "pass" ? "Approved" : "Draft";
      saveGroup(
        nextStatus,
        `Code Group status updated to ${nextStatus}.`,
        true
      );
      return;
    }

    saveGroup("Pending Review", "Code Group submitted for review.", true);
  };

  return (
    <div>
      <TopPanelComponent
        page={page + " - " + formData.groupName}
        version={
          codeGroup != null ? formatVersion(codeGroup.codeGroupVersion) : "1.0"
        }
      />
      <Box sx={{ padding: 1 }}>
        <Grid container spacing={6}>
          {/* Left Column */}
          <Grid size={{ xs: 12, md: 6, lg: 6, xl: 6 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Typography
                sx={{ flex: 1, fontWeight: "bold", textAlign: "left" }}
              >
                Group Name *
              </Typography>
              <Box
                sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}
              >
                <TextField
                  value={formData.groupName}
                  onChange={(e) =>
                    handleChange("groupName", e.target.value.slice(0, 100))
                  }
                  disabled={!isEditable}
                  onBlur={(e) =>
                    handleChange(
                      "groupName",
                      e.target.value.slice(0, 100).trim()
                    )
                  }
                  data-testid="GroupName"
                  required
                  size="small"
                  sx={{ minWidth: "100%" }}
                  slotProps={{
                    input: {
                      maxLength: 100,
                      "data-testid": "Group-Name",
                      id: "Group-Name",
                    },
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Typography
                sx={{ flex: 1, fontWeight: "bold", textAlign: "left" }}
              >
                Description *
              </Typography>
              <Box
                sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}
              >
                <TextField
                  value={formData.description}
                  onChange={(e) =>
                    handleChange("description", e.target.value.slice(0, 500))
                  }
                  disabled={!isEditable}
                  onBlur={(e) =>
                    handleChange(
                      "description",
                      e.target.value.slice(0, 500).trim()
                    )
                  }
                  required
                  error={Boolean(validationErrors.description)}
                  helperText={validationErrors.description}
                  size="small"
                  sx={{ minWidth: "100%" }}
                  slotProps={{
                    input: {
                      maxLength: 500,
                      "data-testid": "Description",
                    },
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Typography
                sx={{
                  flex: 1,
                  textAlign: "left",
                  fontWeight: "bold",
                  mr: 1,
                }}
              >
                Status:
              </Typography>
              <Typography
                sx={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "flex-start",
                }}
                data-testid="StatusValue"
              >
                {formData.codeGroupStatus}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Typography
                sx={{ flex: 1, fontWeight: "bold", textAlign: "left" }}
              >
                Variance
              </Typography>

              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "flex-start",
                }}
              >
                <FormControl component="fieldset">
                  <RadioGroup
                    disabled={!isEditable && !isApprovedAndVariance}
                    row
                    value={formData.isVariance ? true : false}
                    onChange={(e) => {
                      const nextValue = e.target.value === "true";
                      if (varianceLocked && !nextValue) return;
                      setFormData((prev) => ({
                        ...prev,
                        isVariance: nextValue,
                      }));
                      setHealthPlans([]);
                      if (!nextValue) {
                        fetchActivePolicies([AHCS_HEALTH_PLAN_KEY], false);
                      } else {
                        fetchActivePolicies([], true);
                      }
                    }}
                  >
                    <FormControlLabel
                      value={true}
                      control={<Radio />}
                      label="Yes"
                      disabled={!isEditable && !isApprovedAndVariance}
                    />
                    <FormControlLabel
                      value={false}
                      control={<Radio />}
                      label="No"
                      // Disable "No" when health plans are selected
                      disabled={!isEditable || varianceLocked}
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
            </Box>

            {varianceRequired && (
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography
                  sx={{ flex: 1, fontWeight: "bold", textAlign: "left" }}
                >
                  Healthplan(s)
                </Typography>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <HealthPlanMultiSelect
                    open={true}
                    selectedHealthPlans={healthPlans}
                    disabled={!canEditHealthPlans}
                    onChange={(newPlans) => {
                      if (!canEditHealthPlans) return;
                      setHealthPlans(newPlans);
                      // When health plans are selected, ensure Variance is Yes
                      if (Array.isArray(newPlans) && newPlans.length > 0) {
                        setFormData((prev) => ({
                          ...prev,
                          isVariance: true,
                        }));
                      }
                      fetchActivePolicies(newPlans, true);
                    }}
                  />
                </Box>
              </Box>
            )}
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Typography
                sx={{ flex: 1, fontWeight: "bold", textAlign: "left" }}
              >
                Effective With Policy Version *
              </Typography>
              <Box
                sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}
              >
                <Autocomplete
                  multiple
                  options={activePolicies} // pass the objects
                  getOptionLabel={(option) =>
                    `${option.policyNumber} - ${option.policyTitle} - v${option.versionText}`
                  }
                  isOptionEqualToValue={(opt, val) =>
                    opt.policyKey === val.policyKey
                  }
                  renderOption={(props, option) => (
                    <li {...props} key={option.policyKey}>
                      {`${option.policyNumber} - ${option.policyTitle} - v${option.versionText}`}
                    </li>
                  )}
                  value={formData.effectiveVersions
                    .map((v) =>
                      activePolicies.find((p) => p.policyKey === v.policyKey)
                    )
                    .filter(Boolean)}
                  onChange={(e, value) =>
                    handleChangeForEffectiveVersions(
                      value.map((p) => ({ policyKey: p.policyKey }))
                    )
                  }
                  disabled={
                    (!isEditable && isDraft) ||
                    isPendingReview ||
                    hasEffectiveDate
                  }
                  sx={{ minWidth: "100%" }}
                  renderInput={(params) => (
                    <TextField {...params} size="small" />
                  )}
                />
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Typography
                sx={{ flex: 1, fontWeight: "bold", textAlign: "left" }}
              >
                Effective Date *
              </Typography>
              <Box
                sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={formData.effectiveDate}
                    onChange={(newValue) =>
                      handleChange("effectiveDate", newValue)
                    }
                    disabled={
                      !isEditable ||
                      hasEffectiveVersions ||
                      hasLinkedPolicies ||
                      !parentHasLinkedPolicies ||
                      (!isDraft && !isPendingReview)
                    }
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: { minWidth: "100%" },
                        inputProps: { "data-testid": "effective-date-input" },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </Box>

            {/* Type Of Change - visible when Effective Date is present  */}
            {formData.effectiveDate && (
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography
                  sx={{ flex: 1, fontWeight: "bold", textAlign: "left" }}
                >
                  Type of Change *
                </Typography>
                <Box
                  sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}
                >
                  <FormControl size="small" sx={{ minWidth: "100%" }}>
                    <Select
                      value={
                        formData.typeOfChange?.categoryOfWorkName ||
                        formData.typeOfChange ||
                        ""
                      }
                      onChange={(e) => {
                        const selected = typeofchangePolicies.find(
                          (p) => p.categoryOfWorkName === e.target.value
                        );
                        handleChange(
                          "typeOfChange",
                          selected || e.target.value
                        );
                      }}
                      disabled={!isEditable}
                      displayEmpty
                    >
                      {typeofchangePolicies
                        .filter((policy) => policy.codeGroupEnabled)
                        .map((policy) => (
                          <MenuItem
                            key={policy.bulkActionWorkflowReferenceKey}
                            value={policy.categoryOfWorkName}
                          >
                            {policy.categoryOfWorkName}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            )}

            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Typography
                sx={{ flex: 1, fontWeight: "bold", textAlign: "left" }}
              >
                Change Description *
              </Typography>
              <Box
                sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}
              >
                <TextField
                  value={formData.changeDescription}
                  onChange={(e) =>
                    handleChange("changeDescription", e.target.value)
                  }
                  disabled={!isEditable}
                  onBlur={(e) =>
                    handleChange("changeDescription", e.target.value.trim())
                  }
                  required
                  error={Boolean(validationErrors.changeDescription)}
                  helperText={validationErrors.changeDescription}
                  size="small"
                  sx={{ minWidth: "100%" }}
                  slotProps={{
                    input: {
                      "data-testid": "ChangeDescription",
                    },
                  }}
                />
              </Box>
            </Box>

            <ActionButton
              variant="outlined"
              color="inherit"
              aria-label="Create Variance"
              disabled={
                disableCreateVarianceAndCreateNewVersion ||
                loading ||
                !canEditOrCreate
              }
              onClick={async (e) => {
                e.preventDefault();
                if (!isLatest) {
                  setNewVariancePrompt(true);
                  return;
                }
                if (!loading) await createVariance();
              }}
            >
              Create Variance
            </ActionButton>

            <ActionButton
              variant="outlined"
              color="inherit"
              aria-label="Create New Version"
              disabled={
                !canEditOrCreate ||
                !isLatest ||
                disableCreateVarianceAndCreateNewVersion
              }
              onClick={async (e) => {
                e.preventDefault();
                if (!loading) await createNewVersion();
              }}
            >
              Create New Version
            </ActionButton>

            {/* Diagnosis Codes box moved to right column below buttons, only for Diagnosis Groups */}
          </Grid>

          {/* Right Column */}

          <Grid size={{ xs: 12, md: 6, lg: 6, xl: 6 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "flex-start",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  width: 700,
                  justifyContent: "flex-end",
                }}
              >
                <ActionButton
                  variant="outlined"
                  color="inherit"
                  onClick={handleCancel}
                >
                  CANCEL
                </ActionButton>
                <ActionButton
                  variant="outlined"
                  color="inherit"
                  disabled={loading || isApproved}
                  onClick={async (e) => {
                    e.preventDefault();
                    // If Variance is required (forced or selected), Health Plans cannot be blank
                    if (
                      varianceRequired &&
                      canEditHealthPlans &&
                      (!healthPlans || healthPlans.length === 0)
                    ) {
                      dispatch(
                        setDialog({
                          title: "Validation Error",
                          message:
                            "Health Plans must be selected when Variance is set to Yes.",
                        })
                      );
                      return;
                    }
                    if (!loading) {
                      const effectiveStatus =
                        isPendingReview && reviewData.results === "inProgress"
                          ? "Draft"
                          : formData.codeGroupStatus || "Draft";
                      await saveGroup(effectiveStatus);
                    }
                  }}
                >
                  SAVE
                </ActionButton>
                <ActionButton
                  variant="contained"
                  color="primaryButton"
                  disableElevation
                  disabled={!isEditable && !isApprovedAndVariance}
                  onClick={async (e) => {
                    e.preventDefault();

                    if (!loading) {
                      if (isApprovedAndVariance) {
                        if (!healthPlans || healthPlans.length === 0) {
                          dispatch(
                            setDialog({
                              title: "Validation Error",
                              message:
                                "Health Plans must be selected when Variance is set to Yes.",
                            })
                          );
                          return;
                        }
                        saveHpChanges();
                      } else submitForReview();
                    }
                  }}
                >
                  SUBMIT
                </ActionButton>
              </Box>
            </Box>
            <Grid container>
              <Grid size={{ xs: 12, md: 9, lg: 9, xl: 9 }}>
                <Box sx={{ mt: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      size="small"
                      onClick={handleAddEditCodes}
                      sx={{ fontWeight: "bold" }}
                      disabled={!isEditable}
                    >
                      Add/Edit Codes
                    </Button>
                  </Box>
                  {page === "Procedure Groups" && (
                    <SectionBox
                      title={
                        haveInactiveCodes ? (
                          <Typography component="span">
                            Procedure Codes
                            <Tooltip title="Invalid Code">
                              <div style={{ display: "inline-block" }}>
                                <PoetIcon
                                  iconName="warning"
                                  color="#b22637"
                                  style={{
                                    marginLeft: 4,
                                  }}
                                />
                              </div>
                            </Tooltip>
                          </Typography>
                        ) : (
                          "Procedure Codes"
                        )
                      }
                      items={pxCodeList}
                    />
                  )}
                  {/* Tabs for Condensed/Extended view */}
                  {page === "Diagnosis Groups" && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        maxHeight: "60vh",
                      }}
                    >
                      <Tabs
                        value={diagnosisTab || 0}
                        onChange={(e, v) => setDiagnosisTab(v)}
                        sx={{ mt: 1, width: "100%" }}
                        variant="fullWidth"
                      >
                        <Tab label={<Typography>CONDENSED</Typography>} />
                        <Tab
                          label={
                            haveInactiveCodes ? (
                              <Typography component="span">
                                EXTENDED
                                <Tooltip title="Invalid Code">
                                  <div style={{ display: "inline-block" }}>
                                    <PoetIcon
                                      iconName="warning"
                                      color="#b22637"
                                      style={{
                                        marginLeft: 4,
                                      }}
                                    />
                                  </div>
                                </Tooltip>
                              </Typography>
                            ) : (
                              "EXTENDED"
                            )
                          }
                        />
                      </Tabs>
                      {diagnosisTab === 0 && (
                        <Paper
                          variant="outlined"
                          sx={{
                            mt: 1,
                            padding: 1,
                            width: "100%",
                            overflow: "auto",
                          }}
                        >
                          {formData.condensedCodes.length === 0
                            ? "No codes added"
                            : formData.condensedCodes.map((code, i) => (
                                <Typography
                                  key={i}
                                  variant="body2"
                                  component="div"
                                  sx={{ fontSize: "1.1rem" }}
                                >
                                  {code.description?.length > 0
                                    ? code.code + " - " + code.description
                                    : code.code}
                                </Typography>
                              ))}
                        </Paper>
                      )}
                      {diagnosisTab === 1 && (
                        <Paper
                          variant="outlined"
                          sx={{
                            mt: 1,
                            padding: 1,
                            width: "100%",
                            overflow: "auto",
                          }}
                        >
                          {formData.expandedCodes.length === 0
                            ? "No codes added"
                            : formData.expandedCodes.map((code, i) => (
                                <Typography
                                  key={i}
                                  variant="body2"
                                  sx={{ fontSize: "1.1rem" }}
                                >
                                  {code.code}{" "}
                                  {code.description.length > 0
                                    ? "- " + code.description
                                    : ""}
                                  {code.inactive ? (
                                    <div style={{ display: "inline-block" }}>
                                      <Tooltip title="Invalid Code">
                                        <div
                                          style={{ display: "inline-block" }}
                                        >
                                          <PoetIcon
                                            iconName="warning"
                                            color="#b22637"
                                            style={{ marginLeft: 4 }}
                                          />
                                        </div>
                                      </Tooltip>
                                    </div>
                                  ) : (
                                    ""
                                  )}
                                </Typography>
                              ))}
                        </Paper>
                      )}
                    </Box>
                  )}
                  {validationErrors.codes && (
                    <Typography
                      color="error"
                      variant="body2"
                      sx={{ mt: 1 }}
                      data-testid="CodesValidationError"
                    >
                      {validationErrors.codes}
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 3, lg: 3, xl: 3 }}>
                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                  }}
                >
                  <GreyOutButton
                    variant="outlined"
                    size="small"
                    disabled={false}
                    onClick={() => handleDrawerButtonClick("ctReviews")}
                    sx={{ width: 125 }}
                  >
                    C&T Reviews
                  </GreyOutButton>
                  <GreyOutButton
                    sx={{ width: 125, mt: 1 }}
                    variant="outlined"
                    size="small"
                    disabled={false}
                  >
                    History
                  </GreyOutButton>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {activeDrawer && (
          <Drawers
            policyVersion={""}
            editable={isEditable}
            activeDrawer={activeDrawer}
            closeDrawer={closeDrawer}
            healthPlanKey={0}
            policyKey={0}
            status={formData.codeGroupStatus}
            lobAdoptionDecisions={[]}
            policyHistoryData={[]}
            setPolicyHistoryData={setPolicyHistoryData}
            ctReviewData={ctReviewRows}
            reviewData={reviewData}
            ctReviewEditable={canUpdateCtReview}
            setReviewData={setReviewData}
          />
        )}

        {/* Dialog for Diagnosis Codes */}
        {dialogOpen && (
          <DiagnosisCodeDialog
            open={dialogOpen}
            dxCodes={{
              dxCodesCondensed: formData.condensedCodes.join(","),
              dxCodesExpanded: formData.expandedCodes.map((code) => code.code),
            }}
            onClose={() => setDialogOpen(false)}
            onSave={handleSaveDx}
            readOnly={false}
          />
        )}
        {/* Dialog for Procedure Codes */}
        {isPxDialogOpen && (
          <ProcedureCodeDialog
            open={isPxDialogOpen}
            onClose={() => setIsPxDialogOpen(false)}
            onSave={handleAddPxCodesToGrid}
            existingCodes={pxCodeList}
          />
        )}

        {/* Alert dialog prompting navigation to latest version */}
        {newVersionPrompt.open && (
          <Dialog
            open={newVersionPrompt.open}
            onClose={() =>
              setNewVersionPrompt({ open: false, latestKey: null })
            }
          >
            <DialogTitle>Newer Version Required</DialogTitle>
            <DialogContent>
              <Typography component="div">
                {`New versions of this ${
                  page === "Procedure Groups" ? "procedure" : "diagnosis"
                } coding group must be created from the current latest version.`}
              </Typography>
              <Typography sx={{ mt: 2 }}>
                Navigate to the latest version?
              </Typography>
            </DialogContent>
            <DialogActions>
              <ActionButton
                variant="outlined"
                color="button2025RebrandSecondary"
                onClick={() =>
                  setNewVersionPrompt({ open: false, latestKey: null })
                }
              >
                No
              </ActionButton>
              <ActionButton
                variant="contained"
                color="button2025RebrandPrimary"
                onClick={() => {
                  const key = newVersionPrompt.latestKey;
                  if (key) {
                    window.location.href = `/coding-group-editor/${key}?page=${encodeURIComponent(
                      page || ""
                    )}`;
                  }
                }}
              >
                Yes
              </ActionButton>
            </DialogActions>
          </Dialog>
        )}

        {showVariancePrompt && (
          <LatestVersionDialog
            open={showVariancePrompt}
            onClose={() => setNewVariancePrompt(false)}
            onConfirm={async () => {
              if (!loading) await createVariance();
            }}
          />
        )}
      </Box>
    </div>
  );
}

// Reusable section box
function SectionBox({ title, items }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        maxHeight: "60vh",
        height: "100%",
      }}
    >
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
        {title}
      </Typography>
      <Paper
        role="presentation"
        variant="outlined"
        data-testid="PxCodeList"
        sx={{
          flex: 1,
          minHeight: 0,
          maxHeight: "100%",
          overflow: "auto",
          padding: 1,
        }}
      >
        {items.length === 0 ? (
          <Typography variant="body2" sx={{ fontSize: "1.1rem" }}>
            No codes added
          </Typography>
        ) : (
          items.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                width: "100%",
                mb: 0.1,
              }}
            >
              <Typography variant="body2" sx={{ fontSize: "1.1rem" }}>
                {item.code} {item.description ? "- " + item.description : ""}
                {item.inactive ? (
                  <Tooltip title="Invalid Code">
                    <div style={{ display: "inline-block" }}>
                      <PoetIcon
                        iconName="warning"
                        color="#b22637"
                        style={{ marginRight: 4 }}
                      />
                    </div>
                  </Tooltip>
                ) : (
                  ""
                )}
              </Typography>
            </Box>
          ))
        )}
      </Paper>
    </Box>
  );
}

// Helper used solely for display so that integer versions like 2 render as "2.0"
function formatVersion(v) {
  if (v == null) return "";
  const num = Number(v);
  if (!Number.isFinite(num)) return String(v);
  return num % 1 === 0 ? num.toFixed(1) : String(num);
}

function getCtReviewRows(groupDetails) {
  if (!groupDetails || typeof groupDetails !== "object") {
    return [];
  }

  const keyWithCtReviews = Object.keys(groupDetails).find((key) => {
    if (!key) return false;
    return (
      key.toLowerCase().includes("ctreview") && Array.isArray(groupDetails[key])
    );
  });

  if (keyWithCtReviews) {
    return groupDetails[keyWithCtReviews] || [];
  }

  return Array.isArray(groupDetails.ctReviewData)
    ? groupDetails.ctReviewData
    : [];
}

// Convert Dayjs to LocalDate array [year, month(1-12), day]
function toLocalDateArray(d) {
  return [d.year(), d.month() + 1, d.date()];
}

// Convert Dayjs to LocalDateTime array [year, month, day, hour, minute, second, nano]
function toLocalDateTimeArray(d) {
  // Java expects nanoseconds; Dayjs only has milliseconds
  return [
    d.year(),
    d.month() + 1,
    d.date(),
    d.hour(),
    d.minute(),
    d.second(),
    d.millisecond() * 1_000_000,
  ];
}

// Convert possible string/number to integer, else null
function toIntOrNull(v) {
  if (typeof v === "boolean") return null;
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}
